/**
 * Small glyphs shared by task rows, the side sheet and the full-page view:
 * the status circle, priority bars, label badges and the board tile.
 *
 * Status and label colours follow resend-styles: a tone's a11 step for ink,
 * its a3 step for badge fills, with the Radix dark steps under `dark:`.
 */

import { useLayoutEffect, useRef, type CSSProperties } from "react"
import { cn } from "@/lib/utils"
import { TASK_STATE_LABELS, labelTone, priorityLabel, type BadgeTone, type TaskState } from "@/lib/task-boards"
import {
  DISC_RADIUS,
  PIE_CIRCUMFERENCE,
  PIE_RADIUS,
  STATUS_INK,
  STATUS_POSES,
  poseStyle,
  statusMorphTweens,
  type StatusLayer,
} from "@/lib/status-morph"
import type { Tag } from "@/types/task.types"

// ── Status ────────────────────────────────────────────────────────────────

type InkStyle = CSSProperties & { "--status-ink": string; "--status-ink-dark": string }

// A row that changes group remounts, so its circle starts life in the new
// status. The outgoing circle leaves its status here under the caller's
// `morphKey`, and the incoming one, mounting in the same commit, morphs from
// it. Older entries are stale: the row went away (a filter, a collapse) and
// whatever mounts later under that key is not a status change.
const HANDOFF_WINDOW_MS = 100
const handoff = new Map<string, { state: TaskState; at: number }>()

function takeHandoff(key: string | undefined): TaskState | null {
  if (!key) return null
  const entry = handoff.get(key)
  handoff.delete(key)
  return entry && performance.now() - entry.at < HANDOFF_WINDOW_MS ? entry.state : null
}

function leaveHandoff(key: string, state: TaskState) {
  const now = performance.now()
  if (handoff.size > 200) {
    for (const [k, v] of handoff) if (now - v.at >= HANDOFF_WINDOW_MS) handoff.delete(k)
  }
  handoff.set(key, { state, at: now })
}

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true
}

/**
 * The status circle. It is one SVG whose layers take a pose per status, so a
 * change of `state` morphs (pie sweep, ink crossfade, disc fill, check draw,
 * pop) instead of swapping icons. Nothing re-renders for the motion: the new
 * pose renders as usual and the Web Animations API plays from the old one.
 *
 * `morphKey` (e.g. `row:42`) lets the morph survive a remount, for a row that
 * moves to another group when its status changes.
 */
export function StatusIcon({
  state,
  size = 14,
  className,
  morphKey,
}: {
  state: TaskState
  size?: number
  className?: string
  morphKey?: string
}) {
  const svgRef = useRef<SVGSVGElement>(null)
  const shownRef = useRef<TaskState | null>(null)
  const runningRef = useRef<Animation[]>([])

  // No cleanup: an unmounted circle's animations go with its element, and a
  // re-run with the same status (StrictMode's double effects) must leave a
  // running morph alone. A newer change cancels the older morph instead.
  useLayoutEffect(() => {
    const from = shownRef.current ?? takeHandoff(morphKey)
    shownRef.current = state
    const svg = svgRef.current
    if (!from || from === state || !svg || typeof svg.animate !== "function") return
    runningRef.current.forEach((animation) => animation.cancel())

    const tweens = statusMorphTweens(from, state, {
      reducedMotion: prefersReducedMotion(),
      dark: document.documentElement.classList.contains("dark"),
    })
    runningRef.current = tweens.flatMap((tween) => {
      const el = tween.layer === "svg" ? svg : svg.querySelector<SVGElement>(`[data-layer="${tween.layer}"]`)
      if (!el) return []
      return [el.animate(tween.keyframes, {
        delay: tween.delay,
        duration: tween.duration,
        easing: tween.easing,
        // Hold the old pose through the delay, so a later phase doesn't show
        // its end state while an earlier one is still playing.
        fill: "backwards",
      })]
    })
  }, [state, morphKey])

  useLayoutEffect(() => {
    if (!morphKey) return
    return () => {
      if (shownRef.current) leaveHandoff(morphKey, shownRef.current)
    }
  }, [morphKey])

  const pose = STATUS_POSES[state]
  const [ink, inkDark] = STATUS_INK[state]
  const inkStyle: InkStyle = { "--status-ink": ink, "--status-ink-dark": inkDark }
  const layer = (name: StatusLayer) => ({ "data-layer": name, style: poseStyle(name, pose[name]) })
  const background = "hsl(var(--background))"

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      aria-hidden={true}
      className={cn("block shrink-0 text-[color:var(--status-ink)] dark:text-[color:var(--status-ink-dark)]", className)}
      style={inkStyle}
    >
      <circle {...layer("ring")} cx="8" cy="8" r="6.3" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <circle {...layer("dashed")} cx="8" cy="8" r="6.3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2.2 2" />
      {/* Pie: a stroke as wide as its circle, rotated to start at 12 o'clock */}
      <circle
        {...layer("pie")}
        cx="8"
        cy="8"
        r={PIE_RADIUS}
        fill="none"
        stroke="currentColor"
        strokeWidth={PIE_RADIUS * 2}
        strokeDasharray={PIE_CIRCUMFERENCE}
        transform="rotate(-90 8 8)"
      />
      <g {...layer("alert")}>
        <path d="M8 4.8v3.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="8" cy="10.9" r=".9" fill="currentColor" />
      </g>
      <circle
        data-layer="disc"
        cx="8"
        cy="8"
        r={DISC_RADIUS}
        fill="currentColor"
        style={{ ...poseStyle("disc", pose.disc), transformBox: "fill-box", transformOrigin: "center" }}
      />
      <path
        {...layer("check")}
        d="M5 8.2l2 2 4-4.2"
        pathLength={1}
        strokeDasharray="1 1"
        fill="none"
        stroke={background}
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        {...layer("cross")}
        d="M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8"
        pathLength={1}
        strokeDasharray="1 1"
        fill="none"
        stroke={background}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function StatusLabel({ state, size = 14 }: { state: TaskState; size?: number }) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2">
      <StatusIcon state={state} size={size} />
      <span className="truncate">{TASK_STATE_LABELS[state]}</span>
    </span>
  )
}

// ── Priority ──────────────────────────────────────────────────────────────

// Bars lit per priority.
function litBars(priority: string): number {
  if (priority === "high") return 3
  if (priority === "medium") return 2
  return 1
}

export function PriorityIcon({ priority, size = 14, className }: { priority: string; size?: number; className?: string }) {
  const label = `${priorityLabel(priority)} priority`
  if (priority === "urgent") {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label={label} className={cn("block shrink-0", className)}>
        <title>{label}</title>
        <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" className="fill-[#c40006d3] dark:fill-[#ff9592]" />
        <path d="M8 4.6v4.2" stroke="hsl(var(--background))" strokeWidth="1.8" strokeLinecap="round" />
        <circle cx="8" cy="11.3" r="1" fill="hsl(var(--background))" />
      </svg>
    )
  }
  const lit = litBars(priority)
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" role="img" aria-label={label} className={cn("block shrink-0", className)}>
      <title>{label}</title>
      {[4, 7.5, 11].map((h, i) => (
        <rect
          key={h}
          x={1.5 + i * 4.7}
          y={13.5 - h}
          width="3.2"
          height={h}
          rx="1"
          className={i < lit ? "fill-[#333] dark:fill-[#a1a4a5]" : "fill-black/[0.15] dark:fill-[#b0c7d925]"}
        />
      ))}
    </svg>
  )
}

// ── Badges (resend-styles 04) ─────────────────────────────────────────────

const BADGE_TONE_CLASSES: Record<BadgeTone, string> = {
  success: "bg-[#00a43319] text-[#00713fde] dark:bg-[#22ff991e] dark:text-[#46fea5d4]",
  info: "bg-[#008ff519] text-[#006dcbf2] dark:bg-[#0077ff3a] dark:text-[#70b8ff]",
  warning: "bg-[#ffde003d] text-[#ab6400] dark:bg-[#fa820022] dark:text-[#ffca16]",
  error: "bg-[#f3000d14] text-[#c40006d3] dark:bg-[#ff173f2d] dark:text-[#ff9592]",
  violet: "bg-[#4400ee0f] text-[#1f0099af] dark:bg-[#8354fe36] dark:text-[#baa7ff]",
  neutral: "bg-[#eee] text-[#333] dark:bg-[#191b1e] dark:text-[#a1a4a5]",
}

export function LabelBadge({
  tag,
  size = "md",
  onRemove,
  className,
}: {
  tag: Pick<Tag, "id" | "name" | "color">
  size?: "sm" | "md"
  onRemove?: () => void
  className?: string
}) {
  return (
    <span
      className={cn(
        "inline-flex max-w-[140px] shrink-0 items-center gap-1 truncate whitespace-nowrap border border-transparent font-medium",
        size === "sm" ? "h-[22px] rounded-[7px] px-[7px] text-[11.5px]" : "h-6 rounded-lg px-2 text-xs",
        BADGE_TONE_CLASSES[labelTone(tag.color)],
        className,
      )}
      title={tag.name}
    >
      <span className="truncate">{tag.name}</span>
      {onRemove && (
        <button
          type="button"
          aria-label={`Remove ${tag.name}`}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="-mr-0.5 rounded opacity-60 hover:opacity-100"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d="M2 2l6 6M8 2L2 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>
      )}
    </span>
  )
}

// ── Board tile ────────────────────────────────────────────────────────────

export function BoardTile({ name, color, size = 18, className }: { name: string; color: string | null | undefined; size?: number; className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-flex shrink-0 select-none items-center justify-center font-bold leading-none text-white", className)}
      // leading-none centres the letter in the box (the inherited line-height pushed it low); the
      // radius scales with the tile so a 16px one reads as a tile, not a dot.
      style={{ width: size, height: size, fontSize: Math.round(size * 0.56), borderRadius: Math.max(3, Math.round(size * 0.28)), backgroundColor: color || "#6B7280" }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  )
}
